import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { Button } from '@/components/ui/button'
import { LoadingScreen } from '@/components/loading-screen'
import {
  useGetAllJobDescriptions,
  useGetTrendingJobDescriptions,
} from '@/hooks/queries/useJobDescriptions'
import { useReactToJobDescription } from '@/hooks/mutations/useReactToJobDescription'
import { JobDescription } from '@/interfaces/jobDescription'
import { ReactionType } from '@/interfaces/reaction'
import { Images } from '@/assets/Images'
import LibraryJDHeader from './components/LibraryJDHeader'
import TrendingJDSection from './components/TrendingJDSection'
import { getRoleText, getContributorText, getReactionText } from '../../../utils/helpers'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { useGetBUKeywords } from '@/hooks/queries/useBusinessUnits'
import { ReactionModal } from '@/components/ReactionModal'

// Card color variants for the masonry layout - solid colors
const cardColors = ['bg-[#F95396]', 'bg-[#FF9064]', 'bg-[#1C66BB]']

const JDCard = ({
  jd,
  colorClass,
  onReact,
  currentUserId,
  onReactionClick,
}: {
  jd: JobDescription
  colorClass: string
  onReact: (jobDescriptionId: string, reactionType: ReactionType) => void
  currentUserId?: string
  onReactionClick: (jd: JobDescription) => void
}) => {
  const navigate = useNavigate()

  // Format description to show first few lines
  const getShortPreview = (text: string | undefined, maxLength: number = 750) => {
    if (!text) return ''
    const lines = text.split('\n').filter((line) => line.trim())
    let preview = ''
    for (const line of lines) {
      if ((preview + line).length > maxLength) break
      preview += line + '\n'
    }
    return preview.trim() || text.substring(0, maxLength)
  }

  const handleCardClick = () => {
    // If this is the current user's JD, navigate to my-jd, otherwise to detail page
    if (jd.userId === currentUserId) {
      navigate('/my-jd')
    } else {
      navigate(`/jd/${jd.id}`)
    }
  }

  return (
    <div
      className="animate-in fade-in slide-in-from-bottom-4 group hover:shadow-2xl break-inside-avoid relative mb-6 overflow-hidden transition-shadow duration-300 rounded-md cursor-pointer"
      onClick={handleCardClick}
    >
      {/* Header Label */}
      <div className={`${colorClass} px-6 py-6 pb-[80px]`}>
        <h3 className="font-momo-trust-display text-xl leading-tight text-white">
          Untold JD của
          <br />
          {jd.user?.name || 'MoMoer'}
        </h3>
      </div>

      {/* Card Content */}
      <div
        className="relative p-6 -mt-[70px] pt-8 rounded-b-md"
        style={{
          backgroundImage: `url(${Images.background_jd})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* Job Title */}
        <p className="mb-2 text-base">{getRoleText(jd)}</p>

        {/* Divider */}
        <div className="my-4 border-t border-[#BCBCBC]" />

        {/* Description Content */}
        <div className="space-y-2 text-xs font-normal text-[#2F2F2F]">
          <p className="font-semibold text-black">Tố chất nổi bật</p>
          {jd.notableQualities ? (
            <>
              <div className="space-y-0.5">
                {getShortPreview(jd.notableQualities, 500)
                  .split('\n')
                  .map(
                    (line, idx, arr) =>
                      line.trim() && (
                        <p key={idx} className="flex items-start gap-2 leading-tight">
                          <span className="flex-shrink-0">•</span>
                          <span className="pl-2">
                            {line.replace(/^-\s*/, '')}
                            {idx === arr.length - 1 && <span className="text-gray-500">...</span>}
                          </span>
                        </p>
                      )
                  )}
              </div>
              <div className="text-right">
                <button className="text-[#1C66BB] hover:underline mt-2 text-xs font-normal">
                  Xem thêm
                </button>
              </div>
            </>
          ) : (
            <p className="italic text-gray-400">Chưa có mô tả</p>
          )}
        </div>

        <div className="mt-4 border-t border-[#BCBCBC]" />

        {/* Reaction Text */}
        {getReactionText(jd) && (
          <div
            className="hover:text-gray-700 flex items-start gap-1 mt-3 text-xs text-gray-500 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation()
              onReactionClick(jd)
            }}
          >
            {getReactionText(jd)!.iconType === 'both' ? (
              <>
                <img src={Images.icon_heart} alt="heart" className="w-4 h-4" />
                <img src={Images.icon_haha} alt="haha" className="w-4 h-4" />
              </>
            ) : (
              <img
                src={
                  getReactionText(jd)!.iconType === 'heart' ? Images.icon_heart : Images.icon_haha
                }
                alt={getReactionText(jd)!.iconType}
                className="w-3 h-3"
              />
            )}
            <span className="text-xs">
              {getReactionText(jd)!.names.join(', ')}
              {getReactionText(jd)!.remaining > 0 && ` +${getReactionText(jd)!.remaining}`}
            </span>
          </div>
        )}

        {/* Badges/Points */}
        <div className="flex gap-3 mt-3.5">
          <Button
            onClick={(e) => {
              e.stopPropagation()
              onReact(jd.id, ReactionType.HEART)
            }}
            className="border border-primary flex items-center gap-2 px-4 py-2 rounded-full cursor-pointer transition-colors bg-white hover:bg-[#FFEFF4]"
          >
            <img src={Images.icon_heart} alt="heart" className="w-6 h-6" />
            <span className="text-primary text-sm font-normal">{jd.heartCount || 0}</span>
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation()
              onReact(jd.id, ReactionType.HAHA)
            }}
            className="border border-primary flex items-center gap-2 px-4 py-2 rounded-full cursor-pointer transition-colors bg-white hover:bg-[#FFEFF4]"
          >
            <img src={Images.icon_haha} alt="haha" className="w-6 h-6" />
            <span className="text-primary text-sm font-normal">{jd.hahaCount || 0}</span>
          </Button>
        </div>

        {/* User Info */}
        {getContributorText(jd) && (
          <p className="mt-6 text-xs font-normal leading-relaxed text-[#1C66BB]">
            {getContributorText(jd)}
          </p>
        )}
      </div>
    </div>
  )
}

const LibraryJDPage = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()

  // Initialize search query and keywords from URL params
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchParams.get('q') || '')
  const selectedKeywords = useMemo(
    () => searchParams.get('keywords')?.split(',').filter(Boolean) || [],
    [searchParams]
  )
  const isTopTrending = searchParams.get('isTopTrending') === 'true'
  const [selectedBU, setSelectedBU] = useState(searchParams.get('bu') || '')
  const [columns, setColumns] = useState(4)
  const [selectedJD, setSelectedJD] = useState<JobDescription | undefined>(undefined)
  const [isReactionModalOpen, setIsReactionModalOpen] = useState(false)

  // Fetch keywords for the selected department
  const { data: keywordsData } = useGetBUKeywords(selectedBU || undefined)

  const loadMoreRef = useRef<HTMLDivElement>(null)

  // Handle reaction click
  const handleReactionClick = (jd: JobDescription) => {
    setSelectedJD(jd)
    setIsReactionModalOpen(true)
  }

  // Handle toggle trending
  const handleToggleTrending = () => {
    // When toggling trending ON, clear all filters and search
    if (!isTopTrending) {
      setSearchQuery('')
      setSelectedBU('')
      setSearchParams({ isTopTrending: 'true' }, { replace: true })
    } else {
      // When toggling trending OFF, just remove the param
      setSearchParams({}, { replace: true })
    }
  }

  // Handle keyword click - only one keyword at a time
  const handleKeywordClick = (keyword: string) => {
    const params = new URLSearchParams(searchParams)
    const currentKeyword = params.get('keywords')

    // Toggle: if same keyword, remove it; otherwise, replace with new keyword
    if (currentKeyword === keyword) {
      params.delete('keywords')
    } else {
      params.set('keywords', keyword)
    }

    // Keep BU param if exists
    if (selectedBU) {
      params.set('bu', selectedBU)
    }

    setSearchParams(params)
  }

  // Update URL when search query, keywords, or BU change (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)

      // Update URL params
      const params: Record<string, string> = {}
      if (searchQuery.trim()) {
        params.q = searchQuery.trim()
      }
      if (selectedKeywords.length > 0) {
        params.keywords = selectedKeywords.join(',')
      }
      if (selectedBU) {
        params.bu = selectedBU
      }
      // Only keep isTopTrending if not searching or filtering by BU/keywords
      if (isTopTrending && !searchQuery.trim() && selectedKeywords.length === 0 && !selectedBU) {
        params.isTopTrending = 'true'
      }
      setSearchParams(params, { replace: true })
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
  }, [searchQuery, selectedKeywords, selectedBU, isTopTrending, setSearchParams])

  // Fetch data with search query, keywords, and BU filter
  // Disable trending mode when searching or filtering by BU/keywords
  const isActuallyTrending =
    isTopTrending && !debouncedSearchQuery && selectedKeywords.length === 0 && !selectedBU
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetAllJobDescriptions(
      debouncedSearchQuery,
      selectedKeywords.length > 0 ? selectedKeywords : undefined,
      isActuallyTrending,
      selectedBU
    )
  const { data: trendingResponse } = useGetTrendingJobDescriptions()
  const { mutate: reactToJD } = useReactToJobDescription()

  // Extract trending JDs from paginated response
  const trendingJDs = trendingResponse?.data || []

  // Flatten all pages into a single array
  let allJobDescriptions = data?.pages.flatMap((page) => page.data) ?? []

  // Store the original count before filtering
  const totalTrendingCount = isActuallyTrending ? allJobDescriptions.length : 0

  // If actually trending and there are more than 3 trending JDs, filter out the first 3
  if (isActuallyTrending && totalTrendingCount > 3 && trendingJDs.length > 0) {
    const trendingIds = trendingJDs.slice(0, 3).map((jd) => jd.id)
    allJobDescriptions = allJobDescriptions.filter((jd) => !trendingIds.includes(jd.id))
  }

  // Handle reaction click
  const handleReact = (jobDescriptionId: string, reactionType: ReactionType) => {
    // Call API and invalidate query to update counts
    reactToJD(
      {
        jobDescriptionId,
        reactionType,
      },
      {
        onSuccess: () => {
          // Invalidate all jobDescriptions queries to refetch updated data
          queryClient.invalidateQueries({ queryKey: ['jobDescriptions'] })
        },
      }
    )
  }

  // Update columns based on screen size
  useEffect(() => {
    const updateColumns = () => {
      if (window.innerWidth >= 1280) setColumns(4)
      else if (window.innerWidth >= 1024) setColumns(3)
      else if (window.innerWidth >= 768) setColumns(2)
      else setColumns(1)
    }

    updateColumns()
    window.addEventListener('resize', updateColumns)
    return () => window.removeEventListener('resize', updateColumns)
  }, [])

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage || isFetchingNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(loadMoreRef.current)

    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <div className="relative flex flex-col w-full min-h-screen">
      {/* Base Background - Fixed */}
      <div
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url(${Images.bg_with_pattern})`,
          backgroundSize: 'cover',
        }}
      />
      {/* Header */}
      <div className="relative z-10">
        <LibraryJDHeader
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          isTopTrending={isActuallyTrending}
          onToggleTrending={handleToggleTrending}
          selectedBU={selectedBU}
          onBUChange={setSelectedBU}
        />
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex-1 w-full px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Title */}
          <div
            className={`animate-in fade-in zoom-in-95 text-center duration-500 ${
              selectedBU && keywordsData?.uniqueKeywords?.length ? 'mb-4' : 'mb-16'
            }`}
          >
            <h1 className="font-momo-trust-display md:text-6xl mb-6 text-5xl font-bold text-black">
              {isActuallyTrending ? (
                <>
                  Untold JD
                  <br />
                  đang được <span className="text-primary">yêu thích</span>
                </>
              ) : selectedBU ? (
                `Untold JD của ${selectedBU}`
              ) : (
                'Untold JD của MoMoers'
              )}
            </h1>
            {debouncedSearchQuery && (
              <p className="black/80 text-lg">
                Kết quả tìm kiếm cho:{' '}
                <span className="font-semibold">"{debouncedSearchQuery}"</span>
              </p>
            )}
          </div>

          {/* Infinite Sliding Keywords - show when BU is selected */}
          {selectedBU &&
            keywordsData &&
            keywordsData.uniqueKeywords &&
            keywordsData.uniqueKeywords.length > 0 && (
              <div className="relative mb-16 overflow-hidden">
                <style>
                  {`
                    @keyframes slideKeywords {
                      0% {
                        transform: translateX(0);
                      }
                      100% {
                        transform: translateX(-50%);
                      }
                    }
                    .keywords-slider {
                      animation: slideKeywords 45s linear infinite;
                      display: flex;
                      gap: 2rem;
                      width: max-content;
                    }
                    .keywords-slider:hover {
                      animation-play-state: paused;
                    }
                  `}
                </style>
                <div className="keywords-slider">
                  {/* Duplicate keywords 3 times for better seamless loop */}
                  {[
                    ...keywordsData.uniqueKeywords,
                    ...keywordsData.uniqueKeywords,
                    ...keywordsData.uniqueKeywords,
                  ].map((keyword, index) => {
                    const isSelected = selectedKeywords.includes(keyword)
                    return (
                      <span
                        key={`keyword-${index}`}
                        onClick={() => handleKeywordClick(keyword)}
                        className={`whitespace-nowrap font-normal text-white text-[30px] cursor-pointer transition-all duration-200 hover:scale-110 ${
                          isSelected ? 'border border-white rounded-lg px-2' : ''
                        }`}
                      >
                        {keyword}
                      </span>
                    )
                  })}
                </div>
              </div>
            )}

          {/* Trending Section - show when not searching, not filtering by BU AND (not in trending mode OR more than 3 JDs) */}
          {!debouncedSearchQuery &&
            !selectedBU &&
            !isLoading &&
            trendingJDs &&
            trendingJDs.length > 0 && (
              <TrendingJDSection
                trendingJDs={trendingJDs}
                onReact={handleReact}
                hideTitle={isActuallyTrending}
              />
            )}

          {/* List Untold JD Title - only show when NOT in trending mode and NOT filtering by BU */}
          {!isLoading &&
            !isActuallyTrending &&
            !selectedBU &&
            allJobDescriptions &&
            allJobDescriptions.length > 0 && (
              <div className="mb-8 text-center">
                <h3 className="md:text-3xl text-2xl font-bold text-black">Untold JD mới nhất</h3>
              </div>
            )}

          {/* Loading State */}
          {isLoading && (
            <div className="py-20 text-center">
              <LoadingScreen message="Đang tải Untold JD..." />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-500/10 backdrop-blur-sm rounded-xl border-red-500/30 animate-in fade-in zoom-in-95 p-8 text-center border">
              <p className="text-lg font-medium text-red-200">Lỗi: {error.message}</p>
              <Button
                onClick={() => navigate('/')}
                className="hover:bg-white/90 mt-6 text-pink-600 bg-white"
              >
                Quay lại trang chủ
              </Button>
            </div>
          )}

          {/* Masonry Grid */}
          {!isLoading &&
            !error &&
            allJobDescriptions &&
            allJobDescriptions.length > 0 &&
            !(isActuallyTrending && totalTrendingCount <= 3) && (
              <>
                {/* Grid container with column offsets */}
                <div className="md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 animate-in fade-in zoom-in-95 grid grid-cols-1 gap-6 duration-500">
                  {Array.from({ length: columns }).map((_, colIndex) => {
                    // Column offset based on column index
                    const columnOffsets = [0, 214, 32, 130]
                    const offset = columnOffsets[colIndex] || 0

                    return (
                      <div
                        key={colIndex}
                        className="flex flex-col gap-6"
                        style={{ marginTop: `${offset}px` }}
                      >
                        {allJobDescriptions
                          .filter((_, index) => index % columns === colIndex)
                          .map((jd, index) => (
                            <JDCard
                              key={jd.id}
                              jd={jd}
                              colorClass={
                                cardColors[(colIndex + index * columns) % cardColors.length]
                              }
                              onReact={handleReact}
                              currentUserId={user?.id}
                              onReactionClick={handleReactionClick}
                            />
                          ))}
                      </div>
                    )
                  })}
                </div>

                {/* Infinite Scroll Trigger */}
                {hasNextPage && (
                  <div ref={loadMoreRef} className="flex justify-center py-12">
                    {isFetchingNextPage && (
                      <div className="flex items-center gap-2 text-white">
                        <span className="animate-spin text-2xl">⏳</span>
                        <span className="text-lg font-semibold">Đang tải thêm...</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

          {/* No Results */}
          {!isLoading && !error && (!allJobDescriptions || allJobDescriptions.length === 0) && (
            <div className="max-w-5xl mx-auto">
              <div className="rounded-3xl p-6 py-12 text-center bg-white shadow-md">
                <span className="block mb-4 text-6xl">📚</span>
                <p className="mb-2 text-xl font-semibold text-gray-800">Chưa có Untold JD nào</p>
                <p className="mb-6 text-gray-600">
                  Hãy là người đầu tiên chia sẻ câu chuyện của bạn!
                </p>
                <div className="flex flex-row items-center justify-center gap-4">
                  <Button onClick={() => navigate('/create-jd')} className="btn-primary">
                    Viết Untold JD của bạn
                  </Button>
                  <Button onClick={() => navigate('/send-jd')} className="btn-primary">
                    Viết Untold JD cho đồng đội
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reaction Modal */}
      <ReactionModal
        isOpen={isReactionModalOpen}
        onClose={() => setIsReactionModalOpen(false)}
        jobDescription={selectedJD}
      />
    </div>
  )
}

export default LibraryJDPage
